import React, { useState, useEffect } from "react";
import { v4 as UUID } from "uuid";
import Alerts from "../../molecules/Alerts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faUser,
  faCheckCircle,
  faPaperPlane,
  faUserTie,
  faMoneyBillWave,
} from "@fortawesome/free-solid-svg-icons";

import {
  addAccountRequest,
  financeApproverDataLoader,
  lineManagerDataLoader,
  getAccounts,
  getAccountTypes,
  getAccountTypesFromAccount,
} from "../../../utils/queries";
import "./HomePage.css";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Validate } from "../../../utils/Validator";
import { Container, Form } from "reactstrap";
import { FilesForm } from "./FilesForm";
import { ManagerSelect } from "./ManagerSelect";

const Home = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [newAccountName, setNewAccountName] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [vendorPassCodeByEmail, setVendorPassCodeByEmail] = useState(false);
  const [contactMobile, setContactMobile] = useState("");
  const [additional, setAdditional] = useState("");
  const [businessJustification, setBusinessJustification] = useState("");
  const [lineManager, setLineManager] = useState(undefined);
  const [financeApprover, setFinanceApprover] = useState(undefined);
  const [selectedAccount, setSelectedAccount] = useState(undefined);
  const [selectedAccountType, setSelectedAccountType] = useState(null);
  const [allAccountTypes, setAllAccountTypes] = useState([]);
  const [isLoadingAccountTypes, setIsLoadingAccountTypes] = useState(false);
  const [file, setFile] = useState(null);
  const [fileInput, setFileInput] = useState(null);
  const [optionSelect, setOptionSelect] = useState([]);

  useEffect(() => {
    const bindSelectedAccountType = async () => {
      if (selectedAccount && selectedAccount.id) {
        setIsLoadingAccountTypes(true);
        setAllAccountTypes([]);
        let response = await getAccountTypesFromAccount(selectedAccount?.id);
        const firstSelectedType = response.data.selectedAccountTypes?.[0];
        if (firstSelectedType) {
          setSelectedAccountType({
            label: firstSelectedType.typeName,
            value: firstSelectedType.typeId,
            imoSuffix: firstSelectedType.imoSuffix,
          });
        }
        setAllAccountTypes(
          response.data.accountTypeLookupLists?.map((type) => ({
            label: type.name,
            value: type.id,
            imoSuffix: type.imoSuffix,
          }))
        );
        setIsLoadingAccountTypes(false);
      }
    };
    bindSelectedAccountType().then();
  }, [selectedAccount]);

  useEffect(() => {
    const onLoad = async () => {
      await bindAllAccountTypes();
    };
    onLoad().then();
  }, []);

  const addAlert = (message, color, timeout = 7000) => {
    let id = UUID();
    const alert = { message, id, color };
    if (timeout) alert.timeout = timeout;
    setAlerts((prev) => [...prev, alert]);
  };

  const validate = () => {
    const missingFields = [
      !Validate.accountSelection(newAccountName, selectedAccount).isValid ? "New account name or Select existing account" : null,
      !Validate.required(contactName, "Contact name").isValid ? "Contact name" : null,
      !Validate.required(email, "Contact email").isValid ? "Contact email" : null,
      !Validate.phoneRequired(contactMobile, vendorPassCodeByEmail, "Contact mobile").isValid ? "Contact mobile" : null,
      !Validate.required(businessJustification, "Business justification").isValid ? "Business justification" : null,
      !Validate.selected(lineManager, "Line manager").isValid ? "Line manager" : null,
      !Validate.selected(financeApprover, "Finance approver").isValid ? "Finance approver" : null,
      !Validate.selected(selectedAccountType, "Account type").isValid ? "Account type" : null,
    ].filter(Boolean);

    if (missingFields.length > 0) {
      addAlert(`Please fill in the following required fields: ${missingFields.join(", ")}`, "error");
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setNewAccountName("");
    setContactName("");
    setEmail("");
    setVendorPassCodeByEmail(false);
    setContactMobile("");
    setAdditional("");
    setBusinessJustification("");
    setLineManager(null);
    setFinanceApprover(null);
    setSelectedAccount(null);
    setSelectedAccountType(null);
    setAllAccountTypes(optionSelect);
    setFile(null);
    if (fileInput) fileInput.value = "";
  };

  const submitted = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      await addAccountRequest({
        email,
        vendorPassCodeByEmail,
        contactName,
        contactMobile: contactMobile === "" || contactMobile == null ? null : `+${contactMobile}`,
        businessJustification,
        additionalEmail: additional,
        financeApprover,
        lineManager,
        accountName: selectedAccount ? selectedAccount.name : null,
        accountId: selectedAccount ? selectedAccount.value : null,
        accountNumber: selectedAccount ? selectedAccount.accountNumber : null,
        token: selectedAccount ? selectedAccount.token : null,
        newAccountName,
        file,
        accountType: selectedAccountType ? {
          typeId: selectedAccountType.value,
          typeName: selectedAccountType.label,
          imoSuffix: selectedAccountType.imoSuffix,
        } : null,
      });

      addAlert(`Request was sent to ${email} successfully`, "success");
      resetForm();
    } catch (error) {
      addAlert(error.message, "danger");
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async (inputValue) => {
    const accounts = await getAccounts(inputValue);
    return accounts.data.map((account) => ({
      ...account,
      label: `${account.name} | ${account.accountNumber} | ${account.shortName} | ${account.countryCode}`,
      value: account.id,
    }));
  };

  const bindAllAccountTypes = async () => {
    setIsLoadingAccountTypes(true);
    try {
      const types = await getAccountTypes();
      const options = types.data.map((type) => ({
        label: type.name,
        value: type.id,
        imoSuffix: type.imoSuffix,
      }));
      setAllAccountTypes(options);
      setOptionSelect(options);
    } finally {
      setIsLoadingAccountTypes(false);
    }
  };

  const onAccountChanged = async (account) => {
    setSelectedAccountType(null);
    setLineManager(null);
    setFinanceApprover(null);
    if (!account) {
      setSelectedAccount(null);
      setAllAccountTypes(optionSelect);
      return;
    }
    setSelectedAccount(account);
  };

  const handleAccountTypeOnChange = (selectedOption) => {
    setSelectedAccountType(selectedOption);
    setFinanceApprover(null);
    setLineManager(null);
  };

  const NoOptionsMessage = () => null;

  return (
    <Container fluid className="hp-page">
      <div className="hp-content">
        {/* Page header */}
      <div className="hp-header">
        <h1 className="hp-header__title">Request Form</h1>
        <p className="hp-header__subtitle">Create a new vendor account request. All fields marked with * are required.</p>
      </div>

      <Alerts alerts={alerts} setAlerts={setAlerts} />

      <Form onSubmit={submitted}>

        {/* ── Section 1: Account Details ── */}
        <div className="hp-section-card">
          <div className="hp-section-card__header">
            <FontAwesomeIcon icon={faBuilding} className="hp-section-card__icon" />
            <div>
              <div className="hp-section-card__title">Account Details</div>
              <div className="hp-section-card__subtitle">Search for an existing account or enter a new account name</div>
            </div>
          </div>
          <div className="hp-section-card__body">
            <div className="hp-field hp-field--full">
              <label className="hp-label">Existing account</label>
              <AsyncSelect
                cacheOptions
                loadOptions={loadAccounts}
                onChange={onAccountChanged}
                components={{ NoOptionsMessage }}
                value={null}
                controlShouldRenderValue={false}
                isClearable={false}
                placeholder="Search to add…"
                classNamePrefix="hp-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              <div className="hp-assign-list">
                {!selectedAccount ? (
                  <div className="hp-assign-empty">No account selected yet</div>
                ) : (
                  <div className="hp-assign-person">
                    <div className="hp-assign-person-info">
                      <span className="hp-assign-name">{selectedAccount.name}</span>
                      <span className="hp-assign-email">
                        {[selectedAccount.accountNumber, selectedAccount.shortName, selectedAccount.countryCode].filter(Boolean).join(' | ')}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="hp-assign-remove"
                      onClick={() => onAccountChanged(null)}
                      aria-label="Remove"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="hp-field">
              <label className="hp-label">New account name{!selectedAccount && " *"}</label>
              <input
                type="text"
                className="hp-input"
                onChange={(e) => setNewAccountName(e.target.value)}
                value={newAccountName}
              />
            </div>
            <div className="hp-field">
              <label className="hp-label">Account type *</label>
              <Select
                isLoading={isLoadingAccountTypes}
                value={null}
                onChange={handleAccountTypeOnChange}
                options={allAccountTypes}
                controlShouldRenderValue={false}
                isClearable={false}
                placeholder="Search to add…"
                classNamePrefix="hp-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              <div className="hp-assign-list">
                {!selectedAccountType ? (
                  <div className="hp-assign-empty">No account type selected yet</div>
                ) : (
                  <div className="hp-assign-person">
                    <div className="hp-assign-person-info">
                      <span className="hp-assign-name">{selectedAccountType.label}</span>
                    </div>
                    <button
                      type="button"
                      className="hp-assign-remove"
                      onClick={() => handleAccountTypeOnChange(null)}
                      aria-label="Remove"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Section 2: Contact & Preferences ── */}
        <div className="hp-section-card">
          <div className="hp-section-card__header">
            <FontAwesomeIcon icon={faUser} className="hp-section-card__icon" />
            <div>
              <div className="hp-section-card__title">Contact & Preferences</div>
              <div className="hp-section-card__subtitle">Vendor contact details and communication preferences</div>
            </div>
          </div>
          <div className="hp-section-card__body">
            <div className="hp-field">
              <label className="hp-label">Contact name *</label>
              <input
                type="text"
                className="hp-input"
                onChange={(e) => setContactName(e.target.value)}
                value={contactName}
              />
            </div>
            <div className="hp-field">
              <label className="hp-label">Contact email *</label>
              <input
                type="email"
                className="hp-input"
                onChange={(e) => setEmail(e.target.value)}
                value={email}
              />
            </div>
            <div className="hp-field hp-field--full">
              <label className="hp-label">Contact mobile{!vendorPassCodeByEmail && " *"}</label>
              <PhoneInput
                value={contactMobile}
                inputClass="hp-input hp-phone-input"
                inputStyle={{ width: "100%", borderRadius: "8px" }}
                onChange={setContactMobile}
                disabled={vendorPassCodeByEmail}
                enableSearch
                placeholder=""
                dropdownStyle={{ zIndex: 9999 }}
              />
            </div>
            <div className="hp-field hp-field--full hp-toggle-row">
              <span className="hp-toggle-label">
                Send the MFA code by e-mail only
              </span>
              <label className="hp-toggle">
                <input
                  type="checkbox"
                  className="hp-toggle__input"
                  checked={vendorPassCodeByEmail}
                  onChange={() => setVendorPassCodeByEmail(!vendorPassCodeByEmail)}
                />
                <span className="hp-toggle__track" />
              </label>
            </div>
          </div>
        </div>

        {/* ── Section 3: Approval & Details ── */}
        <div className="hp-section-card">
          <div className="hp-section-card__header">
            <FontAwesomeIcon icon={faCheckCircle} className="hp-section-card__icon" />
            <div>
              <div className="hp-section-card__title">Approval & Details</div>
              <div className="hp-section-card__subtitle">Select approvers, provide a business justification and an optional note</div>
            </div>
          </div>
          <div className="hp-section-card__body">
            <div className="hp-field hp-field--full">
              <div className="hp-assign-two-col">
                <ManagerSelect
                  value={lineManager}
                  onChange={setLineManager}
                  dataLoader={lineManagerDataLoader(selectedAccountType ? [selectedAccountType.value] : [])}
                  label="Line manager"
                  emptyText="No line manager selected yet"
                  noOptionsMessage={!selectedAccountType ? "Select an account type first" : undefined}
                />
                <ManagerSelect
                  value={financeApprover}
                  onChange={setFinanceApprover}
                  dataLoader={financeApproverDataLoader(selectedAccountType ? [selectedAccountType.value] : [])}
                  label="Finance approver"
                  emptyText="No finance approver selected yet"
                  noOptionsMessage={!selectedAccountType ? "Select an account type first" : undefined}
                />
              </div>
            </div>
            <div className="hp-field hp-field--full">
              <label className="hp-label">Business justification *</label>
              <textarea
                className="hp-input hp-textarea"
                onChange={(e) => setBusinessJustification(e.target.value)}
                value={businessJustification}
                rows={3}
              />
            </div>
            <div className="hp-field hp-field--full">
              <label className="hp-label">Note to the e-mail</label>
              <textarea
                className="hp-input hp-textarea"
                onChange={(e) => setAdditional(e.target.value)}
                value={additional}
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* ── Attach document ── */}
        <div className="hp-field hp-field--full" style={{ marginBottom: "20px" }}>
          
          <FilesForm file={file} setFile={setFile} setFileInput={setFileInput} onError={(msg) => addAlert(msg, "error")} />
        </div>

        {/* ── Submit ── */}
        <button type="submit" className="hp-submit" disabled={loading}>
          {loading ? (
            <span className="hp-submit__spinner" />
          ) : (
            <FontAwesomeIcon icon={faPaperPlane} className="hp-submit__icon" />
          )}
          {loading ? "Sending..." : "Send Request"}
        </button>

      </Form>
      </div>
    </Container>
  );
};

export default Home;
